import { useCallback, useRef } from "react";
import { debounce } from "lodash";
import { reverseGeocode } from "../client/services.gen";
import { toast, Slide } from "react-toastify";
import mapboxgl, { MapMouseEvent, MapTouchEvent, Marker } from "mapbox-gl";
import { countryCodeAllowList } from "@shane-collective/design-system";
import { cannotCreateLocationInBlockedCountryError } from "../strings";
import { CustomMarker } from "../types/types";
import { useAppState } from "../core/appState/UseAppStateZustand";
import { writeToLog } from "../const";
import { useShaneClientContext } from "../core/shaneClient/ShaneClientProvider";

interface UseLongPressMapInteractionProps {
  isSmallScreen: boolean;
  openPopUp: (
    popupContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    locationId: number,
    feature?: any,
    localMarkers?: CustomMarker[],
    localMarker?: Marker
  ) => void;
  popup: mapboxgl.Popup;
  mapContainer: React.MutableRefObject<HTMLDivElement | null>;
  map: React.MutableRefObject<mapboxgl.Map | null>;
  popupContainerRef: React.MutableRefObject<HTMLDivElement | null>;
}

export function useLongPressMapInteraction({
  popup,
  mapContainer,
  map,
  isSmallScreen,
  openPopUp,
}: UseLongPressMapInteractionProps) {
  const {
    setSearchSet,
    setSelectedFeature,
    newMarker,
    setNewMarker,
    setIsLocationDialogOpen,
  } = useAppState.getState();
  const { shaneClient } = useShaneClientContext();

  const LONG_PRESS_DURATION = 300; // Duration in milliseconds
  //@ts-ignore
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const startX = useRef<number>(0);
  const startY = useRef<number>(0);
  const longClickZoomLevel = useRef<number>(0);

  const handleAddLocation = () => {
    // send message to ff to open location dialog
    const { selectedFeature } = useAppState.getState();
    const coords = selectedFeature?.properties?.coordinates;
    try {
      //@ts-ignore
      window.flutter_inappwebview.callHandler(
        "fromWebApp",
        "new_location:" +
          JSON.stringify({ lat: coords.latitude, lng: coords.longitude })
      );
    } catch (e) {
      alert(
        "new_location:" +
          JSON.stringify({ lat: coords.latitude, lng: coords.longitude })
      );
    }
  };

  const handleMapClick = useCallback(
    async (
      e: any,
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>
    ) => {
      writeToLog(["[UseLongPressMapInteraction] running handleMapClick", e]);
      if (!map?.current) return;
      setIsLocationDialogOpen(false);

      var wc = require("which-country");
      const getCountryISO2 = require("country-iso-3-to-2");

      const point = [e.lngLat.lng, e.lngLat.lat];
      let country3 = "";

      const country = wc(point);
      if (country) {
        country3 = country;
      }

      if (!country) {
        const { data } = await reverseGeocode({
          client: shaneClient,
          query: { lat: e.lngLat.lat, lng: e.lngLat.lng },
        });

        if (data) {
          country3 = (data as any).properties.context.country
            .country_code_alpha_3;
        }
      }

      if (!country3) {
        return;
      }

      // Ensure it's not in a banned country
      if (!countryCodeAllowList.includes(getCountryISO2(country3))) {
        toast.error(
          <div
            dangerouslySetInnerHTML={{
              __html: cannotCreateLocationInBlockedCountryError,
            }}
          />,
          {
            hideProgressBar: true,
            transition: Slide,
            position: isSmallScreen ? "top-center" : "top-right",
            pauseOnHover: true,
            autoClose: 7000,
          }
        );
        return;
      }

      const { newMarker } = useAppState.getState();

      // If there's an existing marker, remove it
      if (newMarker) {
        newMarker.remove();
        setNewMarker(null);
      }

      setSearchSet({});

      // Add a marker
      var el = document.createElement("div");
      el.id = "marker";
      el.classList.add("marker");

      const marker = new mapboxgl.Marker(el)
        .setLngLat(e.lngLat)
        //.setPopup(popup)
        .addTo(map.current);

      setNewMarker(marker);

      const newPopup = new mapboxgl.Popup({ offset: 25 });

      newPopup
        //@ts-ignore
        .setLngLat(e.lngLat)
        .setHTML(
          `
          <div style="display: flex; flex-direction: row; column-gap: 10px; align-items: center; ">
            
            <button style="
              width: 160px;
              height: 30px;
              border-radius: 5px;
              border: 1px solid #1A1A3C;
              background: white;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
              cursor: pointer;
            "><h3 style="font-family: 'Rethink Sans', sans-serif; margin: 0;">Add a new location?</h3></button>
          </div>
        `
        )
        .addTo(map.current);

      marker.setPopup(newPopup);

      const elM = newPopup.getElement();

      if (elM) {
        elM.addEventListener("click", handleAddLocation);

        const cleanupListeners = () => {
          el.removeEventListener("click", handleAddLocation);
        };

        marker.on("remove", () => {
          cleanupListeners();
        });
      }

      // setMarkerVisibility({ marker }, true);

      // Create feature
      let tempFeature: any = {
        geometry: { coordinates: [e.lngLat.lng, e.lngLat.lat], type: "Point" },
        properties: {
          coordinates: { latitude: e.lngLat.lat, longitude: e.lngLat.lng },
          show: true,
        },
        type: "Feature",
      };

      // Open popup
      // openPopUp(
      //   popupContainerRef,
      //   -1,
      //   { ...tempFeature },
      //   [{ ...tempFeature }],
      //   marker
      // );

      // Reverse geocode to get address
      // if (!address) {
      //   const { data } = await reverseGeocode({
      //     client: shaneClient,
      //     query: { lat: e.lngLat.lat, lng: e.lngLat.lng },
      //   });

      //   if (data) {
      //     tempFeature.properties = {
      //       ...tempFeature.properties,
      //       address: (data as any)?.properties?.full_address,
      //     };
      //   }
      // } else {
      //   tempFeature.properties = {
      //     ...tempFeature.properties,
      //     address,
      //   };
      // }

      setSelectedFeature(tempFeature);
    },
    [
      map,
      isSmallScreen,
      setSearchSet,
      setSelectedFeature,
      shaneClient,
      setNewMarker,
      setIsLocationDialogOpen,
    ]
  );

  const debouncedHandleClick = useRef(
    debounce(
      async (
        e: any,
        popupContainerRef: React.MutableRefObject<HTMLDivElement | null>
      ) => {
        await handleMapClick(e, popupContainerRef);
      },
      200
    )
  );

  const handleClick = useCallback(
    (
      e: any,
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>
    ) => {
      debouncedHandleClick.current(e, popupContainerRef);
    },
    []
  );

  const handleLongPress = useCallback(
    (
      e: MapMouseEvent | MapTouchEvent,
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>
    ): void => {
      handleClick(e, popupContainerRef);
    },
    [handleClick]
  );

  const handleLongPressStart = useCallback(
    (
      e: MapMouseEvent | MapTouchEvent,
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>
    ): void => {
      if (longPressTimer.current) return;
      if (!map?.current || !mapContainer?.current) return;

      // Remove existing marker if present
      if (newMarker) {
        newMarker.remove();
        setNewMarker(null);
      }

      const { point } = e;
      const rect = mapContainer.current.getBoundingClientRect();

      startX.current = rect.left + point.x;
      startY.current = rect.top + point.y;
      longClickZoomLevel.current = map?.current.getZoom();

      let latestX = startX.current;
      let latestY = startY.current;

      const updatePosition = (event: MouseEvent | TouchEvent) => {
        if ("touches" in event) {
          latestX = event.touches[0].clientX;
          latestY = event.touches[0].clientY;
        } else {
          latestX = event.clientX;
          latestY = event.clientY;
        }
      };

      window.addEventListener("mousemove", updatePosition);
      window.addEventListener("touchmove", updatePosition);

      longPressTimer.current = setTimeout(() => {
        window.removeEventListener("mousemove", updatePosition);
        window.removeEventListener("touchmove", updatePosition);

        if (
          Math.abs(latestX - startX.current) > 10 ||
          Math.abs(latestY - startY.current) > 10 ||
          map.current?.getZoom() !== longClickZoomLevel.current
        ) {
          if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
          }
          return;
        }

        handleLongPress(e, popupContainerRef);
      }, LONG_PRESS_DURATION);
    },
    [handleLongPress, map, mapContainer, newMarker, setNewMarker]
  );

  const handleLongPressEnd = useCallback((): void => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  return {
    handleLongPressStart,
    handleLongPressEnd,
    handleClick,
  };
}
